import { useEffect } from "react"
import { AvatarInput } from "@/components"
import {
  useUpdateOrganizationAvatarApi,
  organizationResponseToOrganization,
} from "@/hooks/api"
import { useToast, useResourceUrl } from "@/hooks"
import { useStore } from "@/store"
import { Organization, Permission } from "@/types"
import { PermissionGuard } from "@/components/Auth"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { useTranslation } from "react-i18next"

interface OrganizationAvatarUploadProps {
  organization: Organization
}

const OrganizationAvatarUpload = ({
  organization,
}: OrganizationAvatarUploadProps) => {
  const avatarUrl = useResourceUrl(organization?.avatar)
  const { alertToast } = useToast()
  const { t } = useTranslation()
  const {
    error: updateAvatarError,
    data: updateAvatarResp,
    fetchFn: updateAvatar,
  } = useUpdateOrganizationAvatarApi(organization.id)
  const setCurrentOrganization = useStore(
    (state) => state.setCurrentOrganization
  )

  useEffect(() => {
    if (!updateAvatarResp || !updateAvatarResp.data) return
    setCurrentOrganization(
      organizationResponseToOrganization(updateAvatarResp.data)
    )
  }, [updateAvatarResp, setCurrentOrganization])

  useEffect(() => {
    if (!updateAvatarError) return
    console.error("Failed to update organization avatar", updateAvatarError)

    alertToast({
      title: "Failed to update avatar",
      description: getUserFacingApiErrorMessage(
        updateAvatarError,
        t,
        updateAvatarResp?.message || "Failed to update avatar"
      ),
    })
  }, [updateAvatarError, updateAvatarResp, alertToast, t])

  const handleFileChange = (files: File[]) => {
    const selectedFile = files[0]
    const formData = new FormData()
    formData.append("avatar", selectedFile as File)
    updateAvatar({ body: formData })
  }

  return (
    <PermissionGuard permission={Permission.OrgChangeLogo} disabled>
      <AvatarInput
        size="w-24 h-24"
        url={avatarUrl}
        name={organization.name}
        subTitle={organization.name}
        onChange={handleFileChange}
      />
    </PermissionGuard>
  )
}

export default OrganizationAvatarUpload
